import React from 'react'
import clsx from 'classnames'
import type { Salon } from 'api/api.types'
import type { CityService } from 'src/store/data.store'
import type { SelectedService } from 'src/store/appointment.store'
import { calcDuration } from 'utils/calcDuration'
import { declOfNum } from 'utils/declOfNum'
import { DateInfo } from 'components/Profile/VisitInfo/DateInfo'
import { SalonInfo } from 'components/Profile/VisitInfo/SalonInfo'
import { ServicesSummaryItem } from 'components/Appointment/ServicesSummaryItem/ServicesSummaryItem'
import styles from './ClientsSummary.module.scss'

export type SummaryService = SelectedService<CityService>

type ServicesShortListProps = {
  services: SummaryService[],
  index?: number,
  className?: string,
  editable?: boolean,
  onItemDelete?: (serviceId: number) => void,
  salonId?: number | null,
}

export const ServicesShortList: React.FC<ServicesShortListProps> = (props) => {
  const { services, index = 0, className, editable = false, onItemDelete, salonId } = props
  return (
    <div className={clsx([styles.services, className])}>
      {services.map((service) => (
        <div key={`${service.id}-${index}`} className={styles.service}>
          <ServicesSummaryItem service={service} salonId={salonId} />
          {editable && <i className={styles.iconDelete} onClick={() => onItemDelete?.(service.id)} />}
        </div>
      ))}
    </div>
  )
}

export const ClientHeader: React.FC<{ title: React.ReactNode }> = ({ title }) => {
  return <div className={styles.header}>{title}</div>
}

/** «1 час 30 мин»; exactly one hour is shown as «60 мин» */
const formatDuration = (duration: number) => {
  const minutes = duration % 60
  const hours = (duration - minutes) / 60
  if (hours === 1 && minutes === 0) {
    return '60 мин'
  }
  return (hours > 0 ? `${hours} ${declOfNum(hours, ['час', 'часа', 'часов'])} ` : '') + `${minutes} мин`
}

type ClientSummaryProps = {
  index: number,
  title?: React.ReactNode,
  totalClients: number,
  services: SummaryService[],
  note?: React.ReactNode,
  date?: string | null,
  time?: string | null,
  salon?: Salon | null,
  className?: string,
  editable?: boolean,
  onItemDelete?: (serviceId: number) => void,
  discountAmount?: number,
}

/** Services, discount and totals of one client of a visit */
export const ClientSummary: React.FC<ClientSummaryProps> = (props) => {
  const {
    index,
    title,
    totalClients,
    services,
    note,
    date,
    time,
    salon,
    className,
    editable = false,
    onItemDelete,
    discountAmount,
  } = props

  const isLast = totalClients === index + 1
  const hasSiblings = totalClients > 1
  const totalCost = services.reduce((carry, service) => {
    const price = salon ? (service.pricesBySalon?.[salon.id] || 0) : Math.min(...(service.cityPrices ?? []))
    return carry + price * service.amount
  }, 0)

  return (
    <div className={clsx(styles.item, className)}>
      {hasSiblings && <ClientHeader title={title} />}
      <div className={styles.servicesContainer}>
        <ServicesShortList
          services={services}
          index={index}
          editable={editable}
          onItemDelete={onItemDelete}
          salonId={salon?.id}
        />
      </div>
      {(discountAmount !== undefined && discountAmount > 0) && (
        <div className={styles.totals}>
          <div className={styles.totalsTitle}>Скидка</div>
          <div className={styles.totalsAttributes}>
            <div className={styles.totalsDuration} />
            <div className={styles.totalsPrice}>
              <span>{-discountAmount}</span>
              <span className={styles.currency}>₽</span>
            </div>
          </div>
        </div>
      )}
      <div className={styles.totals}>
        <div className={styles.totalsTitle}>Итого</div>
        <div className={styles.totalsAttributes}>
          <div className={styles.totalsDuration}>{formatDuration(calcDuration(services))}</div>
          <div className={styles.totalsPrice}>
            <span>{totalCost - (discountAmount || 0)}</span>
            <span className={styles.currency}>₽</span>
          </div>
        </div>
      </div>
      {isLast && (
        <>
          {note && <div className={styles.note}>{note}</div>}
          {date && time && salon && (
            <div className={styles.info}>
              <DateInfo
                className={styles.dateInfo}
                date={date}
                time={time}
                icon={<i className={styles.dateInfoIcon} />}
              />
              <SalonInfo
                className={styles.salonInfo}
                salon={salon}
                icon={<i className={styles.salonInfoIcon} />}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

type SummaryProps = {
  className?: string,
  itemClassName?: string,
  clients: SummaryService[][],
  clientTitle?: (index: number) => React.ReactNode,
  note?: React.ReactNode,
  date?: string | null,
  time?: string | null,
  salon?: Salon | null,
  discountAmount?: number,
}

/** Summaries of all clients of a visit */
export const Summary: React.FC<SummaryProps> = (props) => {
  const { className, itemClassName, clients, clientTitle, note, date, time, salon, discountAmount } = props
  return (
    <div className={clsx([styles.container, className])}>
      {clients.map((services, index) => (
        <ClientSummary
          key={index}
          index={index}
          totalClients={clients.length}
          title={clientTitle ? clientTitle(index) : ''}
          services={services}
          note={note}
          date={date}
          time={time}
          salon={salon}
          className={itemClassName}
          discountAmount={discountAmount}
        />
      ))}
    </div>
  )
}
